/**
 * Timing constants for the email-verification gate (ENG-3797).
 *
 * These are intentionally small and self-contained so the gate's own bounds
 * stay well under the Electron auth poll (`AUTH_POLL_TIMEOUT_MS = 30_000` in
 * protect.tsx). Per spec-fragment.md §4 the "resolving" loader must be bounded
 * (≤ ~8s) and never spin forever — stacking on top of the auth poll would push
 * a user past the acceptable wait before we surface an actionable error.
 */

/**
 * Cadence of the auto-dismiss / resolve poll. The gate re-reads the Auth0
 * `email_verified` claim (spec §3 Path 1) on this interval while it is
 * blocking or resolving, so a user who verifies in another tab proceeds
 * without a manual refresh.
 */
export const EMAIL_VERIFICATION_POLL_INTERVAL_MS = 3_000;

/**
 * Upper bound on the "resolving" (unknown) state. After this elapses with the
 * signal still unresolved we render a terminal error+retry state rather than
 * silently allowing (fail-CLOSED, spec §4).
 */
export const EMAIL_VERIFICATION_RESOLVE_TIMEOUT_MS = 8_000;

/**
 * Client-side resend cooldown. Per spec §7 the server is authoritative; this
 * is a UX hint only and must tolerate a server rejection gracefully.
 */
export const EMAIL_VERIFICATION_RESEND_COOLDOWN_MS = 60_000;

/**
 * How many CONSECUTIVE genuine transport/connection failures the auto-dismiss
 * poll tolerates before the gate surfaces the terminal connection-error screen
 * (ENG-3797 Failure 1). Recoverable silent-auth outcomes (login_required /
 * consent_required / transient timeout — classified by
 * logic.classifyRefreshFailure) do NOT count toward this bound, and ANY
 * successful poll resets the counter to zero.
 *
 * WHY this exists: the previous build flipped to the error screen on the FIRST
 * refresh rejection, so an ordinary `login_required` while the user was still
 * unverified bounced them to "Check your connection" before they ever verified.
 * At the 3s poll cadence, 3 consecutive genuine failures is ~9s of sustained
 * transport failure — long enough to be real, short enough to surface promptly.
 *
 * A still-unverified user produces RECOVERABLE outcomes (which never count here)
 * while the live /userinfo poll waits for them to verify, so they never reach
 * this connection bound; only genuine transport failures count toward it. The
 * gate auto-dismisses the instant /userinfo reports email_verified:true and
 * never forces a re-login (ENG-3797 auth fix).
 */
export const EMAIL_VERIFICATION_MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * Upper bound on the live claim re-check a gated action runs before showing its
 * verify prompt. A hung /userinfo must still surface the prompt (and its
 * Cancel) instead of leaving the click with no visible result.
 */
export const EMAIL_VERIFICATION_ACTION_PRECHECK_TIMEOUT_MS = 3_000;
